/* SpawnAnything — building rendering: instanced toon meshes (outlines: screen-space pass), hardpoint turrets with yawing heads,
   damage feedback, destruction (voxel debris burst, dust, rubble) and the placement ghost.
   Geometry is cached per (recipe key, level, team) via meshBuilding(..., { greedy: true }); one InstancedMesh per cache entry
   (same pattern as the environment props). */
import * as THREE from 'three';
import { meshBuilding, TURRETS } from './buildings.js';
import { ROLE, addOutline, meshAO, meshGreedy, VoxModel, teamColor, DEC } from './voxel.js';
import { compiledFor, rot90 } from './buildingSetup.js';
import { noOutline } from '../render/outlinePass.js';

// per-instance yaw (aYaw) of one part (uHead) around its pivot: hardpoint turret heads and the laser tower head
const HEAD = `
attribute float aYaw; attribute float aPart; uniform float uHead; uniform vec3 uPivot;
void headYaw(inout vec3 p, inout vec3 n){
  if (uHead < 0.0 || abs(aPart - uHead) > 0.5) return;
  float c = cos(aYaw), s = sin(aYaw);
  mat3 R = mat3(c, 0.0, -s, 0.0, 1.0, 0.0, s, 0.0, c);
  p = R * (p - uPivot) + uPivot; n = R * n;
}`;
function makeMats(toonGrad, head, pivot) {
  const uni = { uHead: { value: head }, uPivot: { value: pivot } };
  const body = new THREE.MeshToonMaterial({ vertexColors: true, gradientMap: toonGrad });
  body.onBeforeCompile = sh => {
    Object.assign(sh.uniforms, uni);
    sh.vertexShader = sh.vertexShader.replace('void main() {', HEAD + '\nattribute float aEmit; varying float vEmit;\nvoid main() {\nvEmit = aEmit;')
      .replace('#include <beginnormal_vertex>', '#include <beginnormal_vertex>\nvec3 hp = position; headYaw(hp, objectNormal);')
      .replace('#include <begin_vertex>', 'vec3 transformed = hp;');
    sh.fragmentShader = sh.fragmentShader.replace('void main() {', 'varying float vEmit;\nvoid main() {')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += diffuseColor.rgb * vEmit * 2.2;');
  };
  body.customProgramCacheKey = () => 'bld-body';
  return { body }; // outlines: screen-space pass (src/render/outlinePass.js)
}
const v3 = new THREE.Vector3(), BOX = new THREE.Box3();

/**
 * @param {{ scene: THREE.Scene, toonGrad: THREE.Texture, recipeOf: (key: string) => object,
 *           fx: () => { debris: any, fx: any, shake: (a: number, x: number, z: number) => void, debrisMul: number, shadows: boolean } }} o
 */
export function createBuildingLayer({ scene, toonGrad, recipeOf, fx }) {
  const geoCache = new Map(), turCache = new Map(), rubCache = new Map(), sampCache = new Map();
  const groups = new Map();
  let list = [], st = [], rubble = [], dirty = true;
  const root = new THREE.Group(); scene.add(root);

  function bGeo(key, level, team) {
    const id = key + ':' + level + ':' + team;
    let e = geoCache.get(id);
    if (!e) {
      const c = compiledFor(key, recipeOf(key), level), m = c.model;
      const geo = meshBuilding(m, team, { greedy: true });
      const hp = m.parts.findIndex(p => p.role === ROLE.TURRET);
      const pv = hp > 0 ? m.parts[hp].pivot.map(x => x * m.vs) : [0, 0, 0];
      geo.computeBoundingSphere();
      e = { geo, head: hp > 0 ? hp : -1, pivot: new THREE.Vector3(...pv), c };
      geoCache.set(id, e);
    }
    return e;
  }
  function tGeo(tk, team) {
    const id = tk + ':' + team;
    let e = turCache.get(id);
    if (!e) {
      const m = TURRETS[tk].model();
      const geo = addOutline(meshAO(m, team, { cx: 0, cz: 0, y0: 0 }, 0.05, false, 1.1), m.vs, 1.0);
      const pv = m.parts[m.headPart].pivot;
      e = { geo, head: m.headPart, pivot: new THREE.Vector3(pv[0] * m.vs, pv[1] * m.vs, pv[2] * m.vs) };
      turCache.set(id, e);
    }
    return e;
  }
  // one instanced body per geometry; grown on demand, hidden (count 0) when unused
  function group(id, e) {
    let g = groups.get(id);
    if (!g) { g = { id, e, mats: makeMats(toonGrad, e.head, e.pivot), body: null, cap: 0, n: 0, items: [] }; groups.set(id, g); }
    return g;
  }
  function ensureCap(g, n) {
    if (g.cap >= n && g.body) return;
    const cap = Math.max(4, n * 2);
    if (g.body) { root.remove(g.body); g.body.dispose(); }
    const yaw = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1); yaw.setUsage(THREE.DynamicDrawUsage);
    g.e.geo.setAttribute('aYaw', yaw);
    const body = new THREE.InstancedMesh(g.e.geo, g.mats.body, cap);
    body.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < cap; i++) body.setColorAt(i, new THREE.Color(1, 1, 1));
    body.frustumCulled = false; body.receiveShadow = true;
    root.add(body);
    g.body = body; g.cap = cap; g.yaw = yaw;
  }

  function rebuild() {
    dirty = false;
    for (const g of groups.values()) g.items = [];
    for (let b = 0; b < list.length; b++) {
      const d = list[b], s = st[b];
      if (!s.alive) continue;
      const e = bGeo(d.key, s.level, d.team);
      group('b:' + d.key + ':' + s.level + ':' + d.team, e).items.push({ b, gun: e.head >= 0 ? 0 : -1 });
      const guns = d.lv[s.level - 1].guns;
      for (let k = 0; k < guns.length; k++) if (guns[k].tk) group('t:' + guns[k].tk + ':' + d.team, tGeo(guns[k].tk, d.team)).items.push({ b, gun: k });
    }
    const sh = fx().shadows;
    for (const g of groups.values()) {
      g.n = g.items.length;
      if (g.n) ensureCap(g, g.n);
      if (g.body) { g.body.count = g.n; g.body.castShadow = sh; g.body.visible = g.n > 0; }
    }
  }

  const M = new THREE.Matrix4(), Q = new THREE.Quaternion(), P = new THREE.Vector3(), S = new THREE.Vector3(1, 1, 1), UP = new THREE.Vector3(0, 1, 0), COL = new THREE.Color();
  /** per frame; cull (optional, src/render/culling.js) skips buildings and turrets outside the view */
  function frame(now, cull) {
    if (dirty) rebuild();
    const t = now * 0.001;
    for (const g of groups.values()) {
      if (!g.n) continue;
      const isB = g.id[0] === 'b';
      let k = 0;
      for (let j = 0; j < g.n; j++) {
        const it = g.items[j], d = list[it.b], s = st[it.b];
        if (cull) { const L = d.lv[s.level - 1], h = L ? L.h : 4, r = Math.hypot(d.hw, d.hd, h / 2) + h * 1.5; if (!cull.sphere(d.x, (d.y || 0) + h / 2, d.z, r)) continue; }
        const shake = s.hit > 0 ? s.hit * 0.5 : 0;
        const ox = shake ? Math.sin(t * 61 + it.b) * shake : 0, oz = shake ? Math.cos(t * 53 + it.b * 3) * shake : 0;
        if (isB) {
          P.set(d.x + ox, d.y || 0, d.z + oz); Q.setFromAxisAngle(UP, d.yaw);
          const dmg = 0.55 + 0.45 * s.hp, fl = 1 - Math.min(0.6, s.hit * 2.4);
          g.body.setColorAt(k, COL.setRGB(dmg * fl, dmg * fl, dmg * fl));
          g.yaw.array[k] = it.gun >= 0 ? (s.gy[0] ?? d.yaw) - d.yaw : 0;
        } else {
          const gun = d.lv[s.level - 1].guns[it.gun];
          P.set(gun.bx + ox, gun.by, gun.bz + oz); Q.setFromAxisAngle(UP, gun.byaw);
          g.yaw.array[k] = (s.gy[it.gun] ?? gun.yaw) - gun.byaw;
        }
        M.compose(P, Q, S); g.body.setMatrixAt(k, M); k++;
      }
      g.body.count = k;
      g.body.instanceMatrix.needsUpdate = true; g.yaw.needsUpdate = true;
      if (g.body.instanceColor) g.body.instanceColor.needsUpdate = true;
    }
  }

  /* ---------- destruction: voxel debris from the model, dust, rubble ---------- */
  function samples(key, level, team) {
    const id = key + ':' + level + ':' + team;
    let s = sampCache.get(id);
    if (!s) {
      const m = compiledFor(key, recipeOf(key), level).model, vs = m.vs;
      const step = Math.max(1, Math.floor(m.map.size / 3000)), pos = [], col = [];
      let i = 0;
      for (const [k, v] of m.map) {
        if (i++ % step) continue;
        const [x, y, z] = DEC(k); if (y < 0) continue;
        pos.push((x + 0.5) * vs, (y + 0.5) * vs, (z + 0.5) * vs);
        teamColor(v.c, team, COL); col.push(COL.r, COL.g, COL.b);
      }
      s = { pos: new Float32Array(pos), col: new Float32Array(col), n: pos.length / 3, vs };
      sampCache.set(id, s);
    }
    return s;
  }
  function rubbleGeo(key, level, team) {
    const id = key + ':' + level + ':' + team;
    let g = rubCache.get(id);
    if (!g) {
      const src = compiledFor(key, recipeOf(key), level).model, f = Math.max(1, Math.round(0.5 / src.vs));
      const m = new VoxModel(src.vs * f), cols = new Map();
      for (const [k, v] of src.map) {
        const [x, y, z] = DEC(k); if (y < 0) continue;
        const cx = Math.floor(x / f), cz = Math.floor(z / f), ck = cx * 4096 + cz, c = cols.get(ck);
        if (!c || y < c.y) cols.set(ck, { cx, cz, y, c: v.c });
      }
      for (const c of cols.values()) {
        const hsh = Math.abs(Math.sin(c.cx * 12.9898 + c.cz * 78.233) * 43758.5453) % 1;
        const h = hsh < 0.35 ? 0 : hsh < 0.8 ? 1 : 2;
        teamColor(c.c, team, COL); COL.multiplyScalar(0.55 + hsh * 0.15);
        for (let y = 0; y <= h; y++) m.set(c.cx, y, c.cz, COL.getHex());
      }
      g = addOutline(meshGreedy(m, 0, { cx: 0, cz: 0, y0: 0 }, 1, 1), m.vs, 1.0);
      rubCache.set(id, g);
    }
    return g;
  }
  let rubMats = null;
  function destroy(b) {
    const d = list[b], s = st[b], F = fx();
    // rubble
    if (!rubMats) rubMats = makeMats(toonGrad, -1, new THREE.Vector3());
    const rg = rubbleGeo(d.key, s.level, d.team);
    if (!rg.getAttribute('aYaw')) rg.setAttribute('aYaw', new THREE.Float32BufferAttribute(new Float32Array(rg.getAttribute('position').count), 1));
    const rb = new THREE.Group(); rb.position.set(d.x, d.y || 0, d.z); rb.rotation.y = d.yaw;
    const body = new THREE.Mesh(rg, rubMats.body); body.receiveShadow = true;
    rb.add(body); root.add(rb); rubble.push(rb);
    // voxel chunks flying out of the model
    const sm = samples(d.key, s.level, d.team), h = d.lv[s.level - 1].h;
    const n = Math.min(sm.n, Math.round((260 + Math.sqrt(d.hw * d.hd) * 40) * F.debrisMul));
    const sz = Math.max(0.18, sm.vs * 1.6);
    for (let k = 0; k < n; k++) {
      const vi = (Math.random() * sm.n) | 0, lx = sm.pos[vi * 3], ly = sm.pos[vi * 3 + 1], lz = sm.pos[vi * 3 + 2];
      const [rx, rz] = rot90(d.rot, lx, lz), dl = Math.hypot(rx, rz) || 1, out = 2 + Math.random() * 6;
      F.debris.spawn(d.x + rx, ly + (d.y || 0), d.z + rz, rx / dl * out + (Math.random() - 0.5) * 3, 3 + Math.random() * 8 + ly * 0.3, rz / dl * out + (Math.random() - 0.5) * 3,
        14 + Math.random() * 10, sz * (0.8 + Math.random() * 1.4), 18, 2 + Math.random() * 8, 0, sm.col[vi * 3], sm.col[vi * 3 + 1], sm.col[vi * 3 + 2]);
    }
    // dust cloud
    const R = Math.max(d.hw, d.hd);
    for (let k = 0; k < 70; k++) {
      const a = Math.random() * 6.283, rr = Math.random() * R, sp = 1 + Math.random() * 3;
      F.fx.spawn(d.x + Math.cos(a) * rr, (d.y || 0) + 0.5 + Math.random() * h * 0.5, d.z + Math.sin(a) * rr, Math.cos(a) * sp, 0.8 + Math.random() * 1.6, Math.sin(a) * sp,
        2 + Math.random() * 1.8, 1.4 + Math.random() * R * 0.35, 0, 1.2, 2, 0.78, 0.72, 0.62);
    }
    F.debris.flush(); F.fx.flush();
    F.shake(R * 1.2 + 4, d.x, d.z);
  }

  /* ---------- placement ghost ---------- */
  const ghostMat = noOutline(new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.5, depthWrite: false }));
  const padMat = noOutline(new THREE.MeshBasicMaterial({ color: 0x3ad16a, transparent: true, opacity: 0.35, depthWrite: false }));
  const ghost = new THREE.Group(), ghostBody = new THREE.Mesh(new THREE.BufferGeometry(), ghostMat), pad = new THREE.Mesh(new THREE.BoxGeometry(1, 0.12, 1), padMat);
  pad.position.y = 0.06; ghost.add(ghostBody, pad); ghost.visible = false; ghost.renderOrder = 3; root.add(ghost);
  let ghostId = '';

  return {
    /** sim data from buildingSimData (setup); resets health/levels/rubble */
    set(sim) {
      list = sim; st = sim.map(d => ({ alive: true, level: d.level, hp: 1, hit: 0, gy: d.lv[d.level - 1].guns.map(g => g.yaw) }));
      for (const r of rubble) root.remove(r);
      rubble = []; dirty = true;
    },
    /** feature:waves — append one building during the battle (bought between waves); the others keep their state */
    add(d) { list.push(d); st.push({ alive: true, level: d.level, hp: 1, hit: 0, gy: d.lv[d.level - 1].guns.map(g => g.yaw) }); dirty = true; },
    /** worker frame: bs = [hpFrac, alive, level, hit] per building, gy = gun yaws of every building's current level */
    update(bs, gy) {
      if (!bs || bs.length !== list.length * 4) return;
      let k = 0;
      for (let b = 0; b < list.length; b++) {
        const s = st[b], o = b * 4, lvl = bs[o + 2] | 0, alive = bs[o + 1] > 0;
        if (lvl !== s.level) { s.level = lvl; dirty = true; }
        s.hp = bs[o]; s.hit = bs[o + 3];
        const ng = list[b].lv[s.level - 1].guns.length;
        if (gy && s.gy.length !== ng) s.gy = new Array(ng).fill(0);
        for (let g = 0; g < ng; g++) s.gy[g] = gy ? gy[k++] : s.gy[g];
        if (s.alive && !alive) { s.alive = false; dirty = true; destroy(b); }
      }
    },
    frame,
    /** building id whose plot contains (x, z), or -1 */
    pick(x, z, aliveOnly = true) {
      for (let b = list.length - 1; b >= 0; b--) {
        const d = list[b]; if (aliveOnly && !st[b].alive) continue;
        if (Math.abs(x - d.x) <= d.hw && Math.abs(z - d.z) <= d.hd) return b;
      }
      return -1;
    },
    /** nearest building whose box (plot x height) the ray hits, or -1 */
    pickRay(ray, aliveOnly = true) {
      let best = -1, bd = Infinity;
      for (let b = 0; b < list.length; b++) {
        const d = list[b], s = st[b]; if (aliveOnly && !s.alive) continue;
        const by = d.y || 0; BOX.min.set(d.x - d.hw, by, d.z - d.hd); BOX.max.set(d.x + d.hw, by + (s.alive ? d.lv[s.level - 1].h : 1), d.z + d.hd);
        if (ray.intersectBox(BOX, v3)) { const dd = v3.distanceToSquared(ray.origin); if (dd < bd) { bd = dd; best = b; } }
      }
      return best;
    },
    state: b => st[b],
    /** placement preview: null hides it */
    ghost(o) {
      if (!o) { ghost.visible = false; return; }
      const id = o.key + ':' + o.level + ':' + o.team;
      if (id !== ghostId) { ghostId = id; ghostBody.geometry = bGeo(o.key, o.level, o.team).geo; }
      const c = bGeo(o.key, o.level, o.team).c;
      ghost.position.set(o.x, o.y || 0, o.z); ghost.rotation.y = (o.rot & 3) * Math.PI / 2;
      pad.scale.set(c.footprint.w, 1, c.footprint.d);
      padMat.color.setHex(o.ok ? 0x3ad16a : 0xe0443a); ghostMat.opacity = o.ok ? 0.55 : 0.3;
      ghost.visible = true;
    },
    /** precompile meshes for a recipe (e.g. right after it was picked) */
    warm(key, level, team) { bGeo(key, level, team); },
    footprint(key, level) { return compiledFor(key, recipeOf(key), level).footprint; },
    shadows(on) { for (const g of groups.values()) if (g.body) g.body.castShadow = on; },
  };
}
